"use client";

import type { ReactNode } from 'react';

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  buttonText: string;
  buttonLink?: string;
  onButtonClick?: () => void;
}

interface GlassPricingProps {
  title?: string;
  subtitle?: string;
  plans: PricingPlan[];
  className?: string;
}

function PricingCard({ plan }: { plan: PricingPlan }) {
  return (
    <div className={`relative bg-white/5 backdrop-blur-sm border rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 ${
      plan.popular ? 'border-orange-400/50 shadow-lg shadow-orange-400/20 scale-105' : 'border-white/10'
    }`}>
      {/* Popular Badge */}
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-orange-400 to-pink-400 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Most Popular
          </span>
        </div>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-white font-bold text-xl mb-2">{plan.name}</h3>
        <div className="mb-2">
          <span className="text-white text-4xl font-bold">{plan.price}</span>
          <span className="text-white/60 text-lg">{plan.period}</span>
        </div>
        <p className="text-white/70 text-sm">{plan.description}</p>
      </div>

      {/* Features List */}
      <ul className="space-y-3 mb-8">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start text-white/80 text-sm">
            <svg
              className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {feature}
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <button
        onClick={() => {
          if (plan.buttonLink) {
            window.location.href = plan.buttonLink;
          } else if (plan.onButtonClick) {
            plan.onButtonClick();
          }
        }}
        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 ${
          plan.popular
            ? 'bg-gradient-to-r from-orange-400 to-pink-400 text-white hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-violet-500/25'
            : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
        }`}
      >
        {plan.buttonText}
      </button>
    </div>
  );
}

export function GlassPricing({ title, subtitle, plans, className = '' }: GlassPricingProps) {
  return (
    <section className={`relative py-24 px-6 ${className}`}>
      {/* Content */}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-16">
            {title && (
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <PricingCard key={index} plan={plan} />
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-12">
          <p className="text-white/60 text-sm">
            All plans include our core features. Upgrade or downgrade at any time.
          </p>
        </div>
      </div>
    </section>
  );
}